import type { Time } from "@suse-o11y/date-time/time";
import { onInterval } from "@suse-o11y/on-interval";
import { useHandler } from "@suse-o11y/use-handler";
import { useLayoutEffect } from "react";

export interface UseIntervalOptions {
	/**
	 * When paused, the setInterval does not run, so nothing forces the hook to trigger re-render.
	 *
	 * @default false
	 */
	pause?: boolean;

	/**
	 * When true, the callback is called immediately after the hook is mounted.
	 *
	 * @default false
	 */
	leading?: boolean;
}

/**
 * Custom hook that sets up an interval to call a provided callback function.
 *
 * @param callback - The function to be called at each interval. Receives the current time as an argument.
 * @param dependencies - An array of dependencies that will trigger the interval to be reset when changed.
 * @param interval - The time interval (in milliseconds) at which the callback function should be called.
 * @param options - An optional object to configure the behavior of the interval.
 *
 * @returns void
 */
export function useInterval(
	callback: (now: Time) => void,
	dependencies: ReadonlyArray<unknown>,
	interval: number,
	{ leading = false, pause = false }: UseIntervalOptions = {},
): void {
	const isDisabled = pause || !isFinite(interval);
	const stableCallback = useHandler(callback);

	useLayoutEffect(() => {
		if (isDisabled) {
			return;
		}

		if (leading) {
			stableCallback(Date.now());
		}

		return onInterval(stableCallback, interval);
	}, [interval, isDisabled, leading, stableCallback, ...dependencies]);
}
